import axios from 'axios';

// Create the simplest possible axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'development' 
      ? 'http://localhost:5000/api' 
      : 'https://app-backends.vercel.app/api'),
  timeout: 15000, // Shorter timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});

// Constants
export const TOKEN_HEADER = 'Authorization';
export const TOKEN_PREFIX = 'Bearer ';

// Initialize headers from saved token
const token = localStorage.getItem('token');
if (token) {
  axiosInstance.defaults.headers.common[TOKEN_HEADER] = `${TOKEN_PREFIX}${token}`;
}

// Improved request interceptor
axiosInstance.interceptors.request.use(
  config => {
    // Always get the latest token from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      config.headers[TOKEN_HEADER] = `${TOKEN_PREFIX}${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Add response interceptor to handle auth errors
axiosInstance.interceptors.response.use(
  response => response,
  error => {
    // Handle authentication errors
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.error('Authentication error:', error.response.data);
      // Clear token and redirect to login if needed
      localStorage.removeItem('token');
      // Force reload to clear state and redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Simplified error handler
export const handleApiError = (error) => {
  const message = !error.response 
    ? 'Network error. Please check your connection.'
    : error.response?.data?.error || error.message || 'Unknown error';
  
  return { error: true, message };
};

// Direct login function that bypasses axios for login attempts
export const directLogin = async (email, password) => {
  try {
    // Try with fetch API instead of axios
    const baseURL = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'development' 
        ? 'http://localhost:5000/api' 
        : 'https://app-backends.vercel.app/api');
    const response = await fetch(`${baseURL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }
    
    // If login successful, store token
    if (data.token) {
      localStorage.setItem('token', data.token);
      axiosInstance.defaults.headers.common[TOKEN_HEADER] = `${TOKEN_PREFIX}${data.token}`;
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('Direct login error:', error.message);
    return { success: false, error: error.message };
  }
};

export default axiosInstance; 